<script lang="ts">
  import dayjs from 'dayjs';
  import { _ } from 'svelte-i18n';
  import { get } from 'svelte/store';
  import { onMount } from 'svelte';
  import Close from '../../icons/Close.svelte';
  import {
    AppState,
    SetIconsBackground,
    SetNotification
  } from '../../../etc/stores';
  import MessagePopup from '../../misc/MessagePopup.svelte';
  import ThemedIcon from '../../misc/ThemedIcon.svelte';
  import FreeCoinsCampaignHeader from '../../icons/FreeCoinsCampaignHeader.svelte';
  import Button from '../../form/Button.svelte';
  import Link from '../../misc/Link.svelte';
  import { formatMoney } from '../../../etc/utils';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../../../services/session-manager';
  import { Container } from '../../../services/container';
  import { Auth } from '../../../services/auth';
  import { Logger } from '../../../services/logger';
  import { FreeCoinsCampaign } from '../../../services/free-coins-campaign';
  import { extractSlugFromError } from '../../../services/api';
  import Analytics from '../../../services/analytics';
  import PoweredBy from '../../misc/PoweredBy.svelte';
  import { replaceVariables } from '../../../i18n';
  import RewardCoin from '../../misc/RewardCoin.svelte';
  import Heading from '../../typography/Heading.svelte';
  import Text from '../../typography/Text.svelte';
  import CoinV2 from '../../icons/CoinV2.svelte';
  import AuthConnector from '../../misc/AuthConnector.svelte';
  import flagKeys from '../../../etc/flagKeys';
  import sessionKeys from '../../../etc/sessionKeys';

  const session = Container.get(SessionManager) as SessionManager;
  const freeCoinsCampaign = Container.get(
    FreeCoinsCampaign
  ) as FreeCoinsCampaign;
  const logger = Container.get(Logger).context(
    'FreeCouponAvailable.svelte'
  ) as Logger;
  const auth = Container.get(Auth);
  interface ILimitComponent {
    text: string;
  }

  const {
    coinsReward,
    limitType,
    redeemCounter,
    dateLimit,
    scheduledCustomerLimit,
    customerLimit
  } = $AppState.freeCoinsCampaign;

  const hasLimit = limitType !== 'no-limit' || dateLimit;

  const couponData = {
    moneyValue: coinsReward * 100,
    code: 'TWM*******',
    status: 'active'
  };

  const category = 'free-coins-state';

  let triggerRenderEvent = false;
  let setLoading;
  let handleConnectCustomer;

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`welcome_coin_available_popup.${text}`), values);
  };

  const closeMessage = () => {
    SetNotification('');
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        connectingAccount: false,
        authFlow: 'default'
      }
    }));
    session.remove(AUTH_STORAGE_FLAGS.welcomeCoins);
  };

  const getLimitComponent: () => ILimitComponent = () => {
    if (limitType !== 'no-limit') {
      const limit =
        limitType === 'daily-customer'
          ? scheduledCustomerLimit || customerLimit
          : customerLimit;

      return {
        text: t('welcome_coins_remaining', {
          couponAmount: limit - redeemCounter
        })
      };
    }

    return {
      text: t('expires_on', {
        dateLimit: dayjs(dateLimit).format('MMM D, YYYY')
      })
    };
  };

  const getCampaignRewardMoney = () => {
    return formatMoney($AppState.business.location, coinsReward);
  };

  const getDescription = () => {
    const NAME =
      $AppState.customer.name || $AppState.auth.providerData?.firstName;
    const type = NAME ? 'customer' : 'guest';

    return t(`description.${type}`, {
      NAME,
      MONEY_VALUE: getCampaignRewardMoney()
    });
  };

  const remindMeAtCheckout = () => {
    session.set(sessionKeys.FREE_COINS_REMIND_CHECKOUT, true);
    if (triggerRenderEvent) handleEventRender();
    Analytics.logEvent('click-remind-checkout', category);
    closeMessage();
  };

  const onClickCloseMessage = () => {
    closeMessage();

    if ($AppState.featureFlags.includes(flagKeys.SEGMENT_CLOSE_WELCOME_COINS)) {
      if (triggerRenderEvent) handleEventRender();

      Analytics.logEvent('click-close', category);
    }
    if (!session.get(sessionKeys.FREE_COINS_REMIND_CHECKOUT)) {
      session.set(sessionKeys.FREE_COINS_REMIND_CHECKOUT, true);
      setTimeout(() => {
        const { coinsReward: campaignRewards, alreadyRedeemed } =
          get(AppState).freeCoinsCampaign;
        if (campaignRewards && !alreadyRedeemed) {
          SetNotification('remindWelcomeCoin');
        }
      }, 15000);
    }
  };

  const getRemindText = () => {
    const id = auth.provider === 'shopify' ? 'later' : 'wallet';

    return t(`remind.${id}`);
  };

  const onGAEventClaimSuccess = () => {
    Analytics.logEvent('click-claim-coins-success', category);
  };

  const redeemCoupon = async () => {
    if (setLoading) setLoading(true);

    try {
      await freeCoinsCampaign.claimCampaignCoupon({
        analyticsCategory: category
      });
      onGAEventClaimSuccess();
      SetNotification('paymentCoinSaved', true);
    } catch (err) {
      const errorModal = freeCoinsCampaign.getNotificationByErrorSlug(
        extractSlugFromError(err)
      );
      SetNotification(errorModal, true);

      if (err.status != 400) {
        logger.error(err);
      }
    }
  };

  const onAfterConnectCustomer = async next => {
    if (next.route === '/dashboard') {
      await redeemCoupon();
    }
  };

  const handleEventRender = () => {
    Analytics.logEvent('render', category);
    triggerRenderEvent = false;
  };

  const onClickPowered = () => {
    if (triggerRenderEvent) handleEventRender();
  };

  const handleSubmit = async () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        authFlow: 'free-coins'
      }
    }));
    session.set(AUTH_STORAGE_FLAGS.welcomeCoins, true);

    if (triggerRenderEvent) handleEventRender();

    Analytics.logEvent('click-claim-coins', category);

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      auth.openLoginModal();
    }
  };

  const triggerConnectCustomer = () => {
    SetIconsBackground('light-without-background');

    if ($AppState.auth.connected) {
      redeemCoupon();
    } else {
      handleConnectCustomer();
    }
  };

  onMount(() => {
    SetIconsBackground('light-without-background');
    if (!$AppState.website.connectingAccount) {
      triggerRenderEvent = true;
    }
  });
</script>

<AuthConnector
  {triggerConnectCustomer}
  {onAfterConnectCustomer}
  loggerContext="FreeCouponAvailable.svelte"
  bind:handleConnectCustomer
  bind:setLoading
  let:loading
>
  <MessagePopup>
    <div class="welcome-coin-available--wrapper">
      <div
        class="welcome-coin-available--close"
        on:click={() => onClickCloseMessage()}
      >
        <ThemedIcon>
          <Close />
        </ThemedIcon>
      </div>
      <div class="welcome-coin-available--top">
        <div class="welcome-coin-available--header">
          <FreeCoinsCampaignHeader />
        </div>
        <div class="welcome-coin-available--coin">
          <RewardCoin size="120" value={couponData.moneyValue / 100} />
          <Heading type="h2">{t('title')}</Heading>
        </div>
        {#if hasLimit}
          <div
            class="welcome-coin-available--limit"
            style="background: {`${$AppState.theme.colors.illustration.secondary}1a`}"
          >
            <CoinV2 size="16" color={$AppState.theme.colors.typography} />
            <Text type="small" textAlign="center">
              {getLimitComponent().text}
            </Text>
          </div>
        {/if}
      </div>
      <div class="welcome-coin-available--bottom">
        <Text type="default" textAlign="center">
          {@html getDescription()}
        </Text>
        <Button label={t('btn')} on:click={handleSubmit} {loading} />
        <div class="welcome-coin-available--bottom--link">
          <Link fontSize="14px" lineHeight="20px" on:click={remindMeAtCheckout}>
            {getRemindText()}
          </Link>
        </div>
      </div>
      <PoweredBy onClick={onClickPowered} />
    </div>
  </MessagePopup>
</AuthConnector>

<style lang="scss">
  @use './styles.scss' as *;

  .welcome-coin-available {
    &--wrapper {
      display: flex;
      position: relative;
      flex-flow: column nowrap;
      align-items: center;
      width: 100%;
    }

    &--close {
      position: absolute;
      top: 16px;
      right: 16px;
      cursor: pointer;
    }

    &--header {
      width: 100%;
      height: 64px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      @media screen and (max-width: 468px) {
        height: 48px;
      }
    }

    &--top {
      background-color: var(--twism-theme-freeCoinsCampaign-background);
      width: 100%;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 16px 0;
      box-sizing: border-box;
    }

    &--bottom {
      width: 100%;
      box-sizing: border-box;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding: 0 16px 28px;

      @media screen and (max-width: 468px) {
        padding: 0 16px 22px;
      }

      &--link {
        margin-top: 24px;
        @media screen and (max-width: 468px) {
          margin-top: 16px;
        }
      }

      :global(p) {
        margin: 28px 0;
        @media screen and (max-width: 468px) {
          margin: 22px 0;
        }
      }

      @media screen and (max-height: 620px) {
        &--link {
          margin-top: 16px;
        }

        padding-bottom: 16px;
      }
    }

    &--coin {
      z-index: 2;
      display: flex;
      align-items: center;
      margin-bottom: 40px;

      :global(h2) {
        margin-left: 16px;
        line-height: 32px !important;
        max-width: 176px;
        @media screen and (max-width: 468px) {
          font-size: 21px;
        }
      }
    }

    &--limit {
      width: calc(100% + 32px);
      box-sizing: border-box;
      height: 44px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: -16px;

      :global(p) {
        text-transform: uppercase;
        font-weight: 600 !important;
        line-height: 12px !important;
        letter-spacing: 0.4px;
        margin-left: 4px !important;
      }
    }
  }
</style>
